import Button from '@material-ui/core/Button';
import Checkbox from '@material-ui/core/Checkbox';
import Chip from '@material-ui/core/Chip';
import Grid from '@material-ui/core/Grid';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import TextField from '@material-ui/core/TextField';
import Toolbar from '@material-ui/core/Toolbar';
import Typography from '@material-ui/core/Typography';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import React, {
  Fragment,
  FunctionComponent,
  KeyboardEvent,
  memo,
  useMemo,
  useState,
} from 'react';
import { useHistory } from 'react-router-dom';
import { useAuthUser } from '../../../../contexts/AuthProvider/hooks';
import { useConfig } from '../../../../eliot-components/src/ConfigProvider/hooks';
import { queryStringFromFilters } from './queryString';
import { Filter, StatusesKeys } from '../../types';
import { onlyUnique } from '../../../../utils/helpers';
import { MediaDown } from '../../../../components/MediaQueries/MediaQueries';
import useStyles from './styles';
import { Categories, Props, TextFilterCategory } from './types';
import NumberFilter from './NumberFilter';
import {
  categoriesLabels,
  getCategoriesFromReport,
  hasAllFiltersMinimumLength,
  isFilterActive,
} from './provider';
import {
  alertsCategories,
  statusDescriptions,
  textFilterCategories,
} from './constants';
import { MedisanteTargetSystem } from '../../../TargetSystems/types';
import ClearAllIcon from '@material-ui/icons/ClearAll';
import { IconButton, Tooltip } from '@material-ui/core';
import Visible from '../../../../components/Visible/Visible';
import { InfoOutlined } from '@material-ui/icons';
import { getKnownIssuesByReports } from '../../../Alerts/provider';
import { setDeviceFilters } from '../../../../store/app-slice';
import { useAppDispatch } from '../../../../hooks';
import { useGetReportsQuery } from '../../../../store/api-slice';
import { mobileSortOptions } from '../../config';
import { MediaUp } from '../../../../components/MediaQueries/MediaQueries';

export const calculateFilters = (
  category: string,
  value: string,
  filters: Filter[]
): Filter[] => {
  const filter = { category, value };
  const isActive = isFilterActive(filter, filters);

  if (!isActive) {
    return addFilter(filter, filters);
  }

  return deleteFilter(filter, filters);
};

export const addFilter = (filter: Filter, filters: Filter[]) => {
  const separatedFilters: Filter[] =
    filter.category === 'measurementType'
      ? [{ category: filter.category, value: filter.value }]
      : filter.value
          .replace(/ /g, ',')
          .split(',')
          .map((c) => c.trim())
          .filter((c) => c !== '')
          .map((f) => ({ category: filter.category, value: f }));

  return [...filters, ...separatedFilters].filter(
    onlyUnique((f1, f2) => f1.value === f2.value && f1.category === f2.category)
  );
};

export const deleteFilter = (filter: Filter, filters: Filter[]) => {
  return filters.filter(
    (f) => f.category !== filter.category || f.value !== filter.value
  );
};

const Filters: FunctionComponent<Props> = memo(
  ({
    filters,
    deviceCount,
    measurementCount,
    targetSystems,
    additionalFields = [],
    minLastStatusCode = 0,
    visibleDevices: devices,
    sortOptionId,
    updateSortState,
  }) => {
    const classes = useStyles();
    const history = useHistory();
    const config = useConfig();
    const { authUser } = useAuthUser();
    const dispatchRedux = useAppDispatch();

    const { data: reports = [], isFetching: isLoadingReports } =
      useGetReportsQuery({
        apiBaseURL: config.apiBaseURL,
        orgs: authUser.selectedOrgs,
        filters: [],
      });

    const knownIssues = useMemo(
      () => getKnownIssuesByReports(reports),
      [reports]
    );

    const [anchorEls, setAnchorEls] = useState<{
      [key: string]: HTMLElement;
    }>({});
    const [textValues, setTextValues] = useState<{
      [key: string]: string;
    }>({});

    const [showHelperText, setShowHelperText] = useState<{
      [key: string]: boolean;
    }>({});

    const categories: Categories = getCategoriesFromReport(reports, config);

    const handleChange = (newFilters: Filter[]) => {
      dispatchRedux(setDeviceFilters(newFilters));
      const queryString = queryStringFromFilters(newFilters);
      history.push({ search: queryString });
    };

    const handleTextFieldKeyPress = (
      category: string,
      e: KeyboardEvent<HTMLDivElement>
    ) => {
      if (e.key === 'Enter') {
        const value = textValues[category];

        if (!value) {
          throw Error('value for text filter not found');
        }

        const shouldDisableSubmit = !hasAllFiltersMinimumLength(value);

        if (shouldDisableSubmit) {
          setShowHelperText({ ...showHelperText, [category]: true });
          return;
        }

        handleChange(addFilter({ category, value }, filters));
        setTextValues({ ...textValues, [category]: '' });
      }
    };

    const handleTextFilterChange =
      (category: TextFilterCategory) => (event: any) => {
        setShowHelperText({});

        setTextValues({
          ...textValues,
          [category.id]: event.currentTarget.value,
        });
      };

    const onClickMenuItem = (
      category: string,
      value: string,
      dialogName: string
    ) => {
      const newFilters = calculateFilters(category, value, filters);

      handleChange(newFilters);

      const { [dialogName]: _, ...rest } = anchorEls;
      setAnchorEls(rest);
    };

    const isLoading = isLoadingReports || false;
    let targetSystemNotGoodStatusCodes: MedisanteTargetSystem[] = [];

    if (targetSystems) {
      targetSystemNotGoodStatusCodes = targetSystems
        .filter((ts) => {
          return ts.lastStatusCode && ts.lastStatusCode > minLastStatusCode;
        })
        .sort((t1, t2) => {
          if (
            t1.lastStatusCode &&
            t2.lastStatusCode &&
            t1.lastStatusCode > t2.lastStatusCode
          ) {
            return 1;
          } else if (
            t1.lastStatusCode &&
            t2.lastStatusCode &&
            t1.lastStatusCode < t2.lastStatusCode
          ) {
            return -1;
          }
          return 0;
        });
    }
    const isLinkedFilterActive =
      isFilterActive({ category: 'withGateway', value: 'true' }, filters) ||
      isFilterActive({ category: 'withSensors', value: 'true' }, filters);
    const isUnlinkedFilterActive =
      isFilterActive({ category: 'noGateway', value: 'true' }, filters) ||
      isFilterActive({ category: 'noSensors', value: 'true' }, filters);
    const isTestDeviceFilterActive =
      isFilterActive({ category: 'testDevice', value: 'true' }, filters) ||
      isFilterActive({ category: 'excludeTestDevice', value: 'true' }, filters);
    const isMutedDeviceFilterActive =
      isFilterActive({ category: 'muted', value: 'true' }, filters) ||
      isFilterActive({ category: 'excludeMuted', value: 'true' }, filters);
    const isArchivedFilterActive =
      isFilterActive({ category: 'onlyArchived', value: 'true' }, filters) ||
      isFilterActive({ category: 'includeArchived', value: 'true' }, filters);
    const isStockFilterActive =
      isFilterActive({ category: 'onlyStock', value: 'true' }, filters) ||
      isFilterActive({ category: 'includeStock', value: 'true' }, filters);
    return (
      <>
        <Toolbar
          className={classes.toolbar}
          style={{ paddingLeft: 0, paddingRight: 0 }}
        >
          <Grid
            container
            justifyContent="flex-start"
            direction="row"
            className={classes.filterContainer}
          >
            <MediaDown query="sm">
              <Grid container justifyContent="flex-end" wrap="wrap">
                <Grid item className={classes.totals}>
                  <Typography
                    variant="subtitle1"
                    color="textSecondary"
                    className={classes.deviceCount}
                  >
                    Total device count: {deviceCount}
                  </Typography>
                </Grid>
                {/*<Grid item className={classes.totals}>*/}
                {/*  <Typography*/}
                {/*    variant="subtitle1"*/}
                {/*    color="textSecondary"*/}
                {/*    className={classes.deviceCount}*/}
                {/*  >*/}
                {/*    Total archived device count: {archivedDeviceCount}*/}
                {/*  </Typography>*/}
                {/*</Grid>*/}
                <Grid item className={classes.totals}>
                  <Typography
                    variant="subtitle1"
                    color="textSecondary"
                    className={classes.deviceCount}
                  >
                    Total measurement count: {measurementCount}
                  </Typography>
                </Grid>
              </Grid>
            </MediaDown>

            <Grid item container sm={12} className={classes.marginBottom}>
              <Grid
                item
                container
                alignItems="center"
                alignContent="center"
                className={classes.dropdownFiltersWrapper}
              >
                {Object.keys(categories).map((c) => {
                  const shouldDisable = categories[c].length === 0;
                  const count = getCount(filters, [c]);

                  return (
                    <Fragment key={c}>
                      <Button
                        size="small"
                        disabled={shouldDisable || isLoading}
                        className={classes.filter}
                        aria-owns={anchorEls[c] ? c : undefined}
                        aria-haspopup="true"
                        data-testid={'button-' + c}
                        onClick={(e) => {
                          setAnchorEls({
                            ...anchorEls,
                            [c]: e.currentTarget,
                          });
                        }}
                      >
                        <span className={classes.model}>
                          <span className="label" data-testid={c}>
                            {`${
                              categoriesLabels[c]
                                ? categoriesLabels[c]
                                : c.replace(/([A-Z]+)*([A-Z][a-z])/g, '$1 $2')
                            }`}
                            {count}
                          </span>
                          <KeyboardArrowDownIcon />
                        </span>
                      </Button>

                      <Menu
                        id={c}
                        classes={{ paper: classes.menu }}
                        anchorEl={anchorEls[c]}
                        keepMounted
                        open={Boolean(anchorEls[c])}
                        onClose={() => {
                          const { [c]: _, ...rest } = anchorEls;
                          setAnchorEls(rest);
                        }}
                      >
                        {categories[c].map((o) => (
                          <MenuItem
                            key={o}
                            onClick={() => onClickMenuItem(c, o, c)}
                          >
                            <Checkbox
                              edge="start"
                              checked={isFilterActive(
                                {
                                  category: c,
                                  value: o,
                                },
                                filters
                              )}
                            />
                            <Typography variant="body2">{o}</Typography>
                          </MenuItem>
                        ))}
                      </Menu>
                    </Fragment>
                  );
                })}

                <Button
                  disabled={
                    isLoading ||
                    !knownIssues ||
                    Object.keys(knownIssues).length === 0
                  }
                  size="small"
                  className={classes.filter}
                  aria-owns={anchorEls['alerts'] ? 'alerts' : undefined}
                  aria-haspopup="true"
                  onClick={(e) => {
                    setAnchorEls({
                      ...anchorEls,
                      alerts: e.currentTarget,
                    });
                  }}
                >
                  <span className={classes.model}>
                    <span className="label">
                      Alerts{' '}
                      {getCount(
                        filters,
                        Object.entries(alertsCategories).map(
                          ([_, c]) => c.queryKey
                        )
                      )}
                    </span>
                    <KeyboardArrowDownIcon />
                  </span>
                </Button>

                <Menu
                  id="alerts"
                  classes={{ paper: classes.menu }}
                  anchorEl={anchorEls['alerts']}
                  open={Boolean(anchorEls['alerts'])}
                  onClose={() => {
                    const { alerts: _, ...rest } = anchorEls;
                    setAnchorEls(rest);
                  }}
                >
                  {Object.entries(alertsCategories)
                    .filter(([key, value]) => {
                      const issue = knownIssues[key as StatusesKeys];
                      return issue && issue.deviceCount > 0;
                    })
                    .map(([key, c]) => (
                      <MenuItem
                        key={c.label}
                        onClick={() =>
                          onClickMenuItem(c.queryKey, c.queryValue, 'alerts')
                        }
                      >
                        <Checkbox
                          edge="start"
                          checked={isFilterActive(
                            {
                              category: c.queryKey,
                              value: c.queryValue,
                            },
                            filters
                          )}
                        />
                        <Typography variant="body2">{c.label}</Typography>
                      </MenuItem>
                    ))}
                </Menu>
                <Button
                  disabled={
                    isLoading ||
                    !targetSystems ||
                    (targetSystems &&
                      targetSystemNotGoodStatusCodes.length === 0)
                  }
                  size="small"
                  className={classes.filter}
                  aria-owns={anchorEls['ts-status'] ? 'ts-status' : undefined}
                  aria-haspopup="true"
                  onClick={(e) => {
                    setAnchorEls({
                      ...anchorEls,
                      'ts-status': e.currentTarget,
                    });
                  }}
                >
                  <span className={classes.model}>
                    <span className="label">
                      Last status {getCount(filters, ['last_status_code'])}
                    </span>
                    <KeyboardArrowDownIcon />
                  </span>
                </Button>
                <Menu
                  id="ts-status"
                  classes={{ paper: classes.menu }}
                  anchorEl={anchorEls['ts-status']}
                  open={Boolean(anchorEls['ts-status'])}
                  onClose={() => {
                    const { 'ts-status': _, ...rest } = anchorEls;
                    setAnchorEls(rest);
                  }}
                >
                  {targetSystemNotGoodStatusCodes
                    .filter(
                      onlyUnique(
                        (f1, f2) => f1.lastStatusCode === f2.lastStatusCode
                      )
                    )
                    .map((ts) => (
                      <MenuItem
                        key={ts.lastStatusCode}
                        onClick={() =>
                          onClickMenuItem(
                            'last_status_code',
                            // @ts-ignore
                            ts.lastStatusCode.toString(),
                            'ts-status'
                          )
                        }
                      >
                        <Checkbox
                          edge="start"
                          checked={isFilterActive(
                            {
                              category: 'last_status_code',
                              // @ts-ignore
                              value: ts.lastStatusCode.toString(),
                            },
                            filters
                          )}
                        />
                        <Typography variant="body2">
                          {ts.lastStatusCode}
                          {ts.lastStatusCode &&
                          statusDescriptions[ts.lastStatusCode] ? (
                            <Tooltip
                              title={statusDescriptions[ts.lastStatusCode]}
                            >
                              <IconButton>
                                <InfoOutlined />
                              </IconButton>
                            </Tooltip>
                          ) : null}
                        </Typography>
                      </MenuItem>
                    ))}
                </Menu>
                {updateSortState && (
                  <MediaUp query="md">
                    <Button
                      aria-controls="simple-menu"
                      aria-haspopup="true"
                      size="small"
                      className={classes.filter}
                      onClick={(e) => {
                        setAnchorEls({
                          ...anchorEls,
                          'sort-by': e.currentTarget,
                        });
                      }}
                    >
                      <span className="label">Sorty By</span>
                      <KeyboardArrowDownIcon />
                    </Button>
                    <Menu
                      id="simple-menu"
                      anchorEl={anchorEls['sort-by']}
                      open={Boolean(anchorEls['sort-by'])}
                      onClose={() => {
                        const { 'sort-by': _, ...rest } = anchorEls;
                        setAnchorEls(rest);
                      }}
                    >
                      {mobileSortOptions.map((o) => (
                        <MenuItem
                          key={o.id}
                          selected={o.id === sortOptionId}
                          value={o.id}
                          onClick={(event) => {
                            //@ts-ignore
                            const val = event.target.getAttribute('value');
                            const option = mobileSortOptions.find(
                              (op) => op.id === val
                            );
                            const { 'sort-by': _, ...rest } = anchorEls;
                            setAnchorEls(rest);
                            if (option) {
                              updateSortState(option.value);
                            }
                          }}
                        >
                          {o.label}
                        </MenuItem>
                      ))}
                    </Menu>
                  </MediaUp>
                )}

                <Button
                  disabled={isLoading}
                  size="small"
                  className={classes.filter}
                  aria-owns={
                    anchorEls['link-status'] ? 'link-status' : undefined
                  }
                  aria-haspopup="true"
                  onClick={(e) => {
                    setAnchorEls({
                      ...anchorEls,
                      'link-status': e.currentTarget,
                    });
                  }}
                >
                  <span className={classes.model}>
                    <span className="label">
                      Link Status{' '}
                      {(isUnlinkedFilterActive || isLinkedFilterActive) && (
                        <span>(1)</span>
                      )}
                    </span>
                    <KeyboardArrowDownIcon />
                  </span>
                </Button>
                <Menu
                  id="linked-status"
                  classes={{ paper: classes.menu }}
                  anchorEl={anchorEls['link-status']}
                  open={Boolean(anchorEls['link-status'])}
                  onClose={() => {
                    const { 'link-status': _, ...rest } = anchorEls;
                    setAnchorEls(rest);
                  }}
                >
                  <MenuItem
                    key="Gateway"
                    disabled={
                      !isFilterActive(
                        { category: 'withSensors', value: 'true' },
                        filters
                      ) &&
                      (isUnlinkedFilterActive || isLinkedFilterActive)
                    }
                    onClick={() =>
                      onClickMenuItem('withSensors', 'true', 'link-status')
                    }
                  >
                    <Checkbox
                      edge="start"
                      checked={isFilterActive(
                        { category: 'withSensors', value: 'true' },
                        filters
                      )}
                    />
                    <Typography variant="body2">Linked gateway</Typography>
                  </MenuItem>

                  <MenuItem
                    key="LinkedSensors"
                    disabled={
                      !isFilterActive(
                        { category: 'noSensors', value: 'true' },
                        filters
                      ) &&
                      (isUnlinkedFilterActive || isLinkedFilterActive)
                    }
                    onClick={() =>
                      onClickMenuItem('noSensors', 'true', 'link-status')
                    }
                  >
                    <Checkbox
                      edge="start"
                      checked={isFilterActive(
                        { category: 'noSensors', value: 'true' },
                        filters
                      )}
                    />
                    <Typography variant="body2">Unlinked gateway</Typography>
                  </MenuItem>

                  <MenuItem
                    key="UnlinkedGateway"
                    disabled={
                      !isFilterActive(
                        { category: 'withGateway', value: 'true' },
                        filters
                      ) &&
                      (isUnlinkedFilterActive || isLinkedFilterActive)
                    }
                    onClick={() =>
                      onClickMenuItem('withGateway', 'true', 'link-status')
                    }
                  >
                    <Checkbox
                      edge="start"
                      checked={isFilterActive(
                        { category: 'withGateway', value: 'true' },
                        filters
                      )}
                    />
                    <Typography variant="body2">Linked sensor</Typography>
                  </MenuItem>

                  <MenuItem
                    key="UnlinkedSensors"
                    disabled={
                      !isFilterActive(
                        { category: 'noGateway', value: 'true' },
                        filters
                      ) &&
                      (isUnlinkedFilterActive || isLinkedFilterActive)
                    }
                    onClick={() =>
                      onClickMenuItem('noGateway', 'true', 'link-status')
                    }
                  >
                    <Checkbox
                      edge="start"
                      checked={isFilterActive(
                        { category: 'noGateway', value: 'true' },
                        filters
                      )}
                    />
                    <Typography variant="body2">Unlinked sensor</Typography>
                  </MenuItem>
                </Menu>

                <Button
                  disabled={isLoading}
                  size="small"
                  className={classes.filter}
                  aria-owns={anchorEls['testdevice'] ? 'testdevice' : undefined}
                  aria-haspopup="true"
                  onClick={(e) => {
                    setAnchorEls({
                      ...anchorEls,
                      testdevice: e.currentTarget,
                    });
                  }}
                >
                  <span className={classes.model}>
                    <span className="label">
                      Test Device {isTestDeviceFilterActive && <span>(1)</span>}
                    </span>
                    <KeyboardArrowDownIcon />
                  </span>
                </Button>
                <Menu
                  id="testdevice"
                  classes={{ paper: classes.menu }}
                  anchorEl={anchorEls['testdevice']}
                  open={Boolean(anchorEls['testdevice'])}
                  onClose={() => {
                    const { testdevice: _, ...rest } = anchorEls;
                    setAnchorEls(rest);
                  }}
                >
                  <MenuItem
                    key="ExcludeTestDevice"
                    disabled={isFilterActive(
                      { category: 'testDevice', value: 'true' },
                      filters
                    )}
                    onClick={() =>
                      onClickMenuItem('excludeTestDevice', 'true', 'testdevice')
                    }
                  >
                    <Checkbox
                      edge="start"
                      checked={isFilterActive(
                        { category: 'excludeTestDevice', value: 'true' },
                        filters
                      )}
                    />
                    <Typography variant="body2">Exclude Test Device</Typography>
                  </MenuItem>
                  <MenuItem
                    key="OnlyTestDevice"
                    disabled={isFilterActive(
                      { category: 'excludeTestDevice', value: 'true' },
                      filters
                    )}
                    onClick={() =>
                      onClickMenuItem('testDevice', 'true', 'testdevice')
                    }
                  >
                    <Checkbox
                      edge="start"
                      checked={isFilterActive(
                        { category: 'testDevice', value: 'true' },
                        filters
                      )}
                    />
                    <Typography variant="body2">Only Test Device</Typography>
                  </MenuItem>
                </Menu>
                <Button
                  disabled={isLoading}
                  size="small"
                  className={classes.filter}
                  aria-owns={anchorEls['muted'] ? 'muted' : undefined}
                  aria-haspopup="true"
                  onClick={(e) => {
                    setAnchorEls({
                      ...anchorEls,
                      muted: e.currentTarget,
                    });
                  }}
                >
                  <span className={classes.model}>
                    <span className="label">
                      Muted Device{' '}
                      {isMutedDeviceFilterActive && <span>(1)</span>}
                    </span>
                    <KeyboardArrowDownIcon />
                  </span>
                </Button>
                <Menu
                  id="muted"
                  classes={{ paper: classes.menu }}
                  anchorEl={anchorEls['muted']}
                  open={Boolean(anchorEls['muted'])}
                  onClose={() => {
                    const { muted: _, ...rest } = anchorEls;
                    setAnchorEls(rest);
                  }}
                >
                  <MenuItem
                    key="ExcludeMuted"
                    disabled={isFilterActive(
                      { category: 'muted', value: 'true' },
                      filters
                    )}
                    onClick={() =>
                      onClickMenuItem('excludeMuted', 'true', 'muted')
                    }
                  >
                    <Checkbox
                      edge="start"
                      checked={isFilterActive(
                        { category: 'excludeMuted', value: 'true' },
                        filters
                      )}
                    />
                    <Typography variant="body2">
                      Exclude Muted Device
                    </Typography>
                  </MenuItem>
                  <MenuItem
                    key="Muted"
                    disabled={isFilterActive(
                      { category: 'excludeMuted', value: 'true' },
                      filters
                    )}
                    onClick={() => onClickMenuItem('muted', 'true', 'muted')}
                  >
                    <Checkbox
                      edge="start"
                      checked={isFilterActive(
                        { category: 'muted', value: 'true' },
                        filters
                      )}
                    />
                    <Typography variant="body2">Only Muted Device</Typography>
                  </MenuItem>
                </Menu>

                <Button
                  disabled={isLoading}
                  size="small"
                  className={classes.filter}
                  aria-owns={anchorEls['archived'] ? 'archived' : undefined}
                  aria-haspopup="true"
                  onClick={(e) => {
                    setAnchorEls({
                      ...anchorEls,
                      archived: e.currentTarget,
                    });
                  }}
                >
                  <span className={classes.model}>
                    <span className="label">
                      Archived {isArchivedFilterActive && <span>(1)</span>}
                    </span>
                    <KeyboardArrowDownIcon />
                  </span>
                </Button>
                <Menu
                  id="archived"
                  classes={{ paper: classes.menu }}
                  anchorEl={anchorEls['archived']}
                  open={Boolean(anchorEls['archived'])}
                  onClose={() => {
                    const { archived: _, ...rest } = anchorEls;
                    setAnchorEls(rest);
                  }}
                >
                  <MenuItem
                    key="IncludeArchived"
                    onClick={() =>
                      onClickMenuItem('includeArchived', 'true', 'archived')
                    }
                    disabled={isFilterActive(
                      { category: 'onlyArchived', value: 'true' },
                      filters
                    )}
                  >
                    <Checkbox
                      edge="start"
                      checked={isFilterActive(
                        { category: 'includeArchived', value: 'true' },
                        filters
                      )}
                    />
                    <Typography variant="body2">Include Archived</Typography>
                  </MenuItem>
                  <MenuItem
                    key="OnlyArchived"
                    onClick={() =>
                      onClickMenuItem('onlyArchived', 'true', 'archived')
                    }
                    disabled={isFilterActive(
                      { category: 'includeArchived', value: 'true' },
                      filters
                    )}
                  >
                    <Checkbox
                      edge="start"
                      checked={isFilterActive(
                        { category: 'onlyArchived', value: 'true' },
                        filters
                      )}
                    />
                    <Typography variant="body2">Only Archived</Typography>
                  </MenuItem>
                </Menu>

                <Button
                  disabled={isLoading}
                  size="small"
                  className={classes.filter}
                  aria-owns={anchorEls['stock'] ? 'stock' : undefined}
                  aria-haspopup="true"
                  onClick={(e) => {
                    setAnchorEls({
                      ...anchorEls,
                      stock: e.currentTarget,
                    });
                  }}
                >
                  <span className={classes.model}>
                    <span className="label">
                      Stock {isStockFilterActive && <span>(1)</span>}
                    </span>
                    <KeyboardArrowDownIcon />
                  </span>
                </Button>
                <Menu
                  id="stock"
                  classes={{ paper: classes.menu }}
                  anchorEl={anchorEls['stock']}
                  open={Boolean(anchorEls['stock'])}
                  onClose={() => {
                    const { stock: _, ...rest } = anchorEls;
                    setAnchorEls(rest);
                  }}
                >
                  <MenuItem
                    key="IncludeStock"
                    onClick={() =>
                      onClickMenuItem('includeStock', 'true', 'stock')
                    }
                    disabled={isFilterActive(
                      { category: 'onlyStock', value: 'true' },
                      filters
                    )}
                  >
                    <Checkbox
                      edge="start"
                      checked={isFilterActive(
                        { category: 'includeStock', value: 'true' },
                        filters
                      )}
                    />
                    <Typography variant="body2">Include Stock</Typography>
                  </MenuItem>
                  <MenuItem
                    key="OnlyStock"
                    onClick={() =>
                      onClickMenuItem('onlyStock', 'true', 'stock')
                    }
                    disabled={isFilterActive(
                      { category: 'includeStock', value: 'true' },
                      filters
                    )}
                  >
                    <Checkbox
                      edge="start"
                      checked={isFilterActive(
                        { category: 'onlyStock', value: 'true' },
                        filters
                      )}
                    />
                    <Typography variant="body2">Only Stock</Typography>
                  </MenuItem>
                </Menu>
              </Grid>
              <Grid container item alignItems="flex-start">
                {textFilterCategories.map((c, index) => (
                  <TextField
                    disabled={isLoading}
                    variant="outlined"
                    margin="dense"
                    placeholder="Search"
                    id={c.id}
                    key={c.id}
                    label={c.label}
                    className={`${classes.textFilter}${
                      index === 0 ? ' first' : ''
                    }`}
                    value={textValues[c.id] || ''}
                    onChange={handleTextFilterChange(c)}
                    onKeyPress={(e) => {
                      handleTextFieldKeyPress(c.id, e);
                    }}
                    helperText={
                      showHelperText[c.id]
                        ? 'Every search query must be at least four characters long'
                        : null
                    }
                  />
                ))}
                <NumberFilter
                  disabled={isLoading}
                  label="Measurement count"
                  className={classes.textFilter}
                  addFilter={(separatedFilters: Filter[]) => {
                    const newFilters = [...filters, ...separatedFilters].filter(
                      onlyUnique(
                        (f1, f2) =>
                          f1.value === f2.value && f1.category === f2.category
                      )
                    );
                    handleChange(newFilters);
                  }}
                />
                {additionalFields}
              </Grid>
            </Grid>
            <Grid item sm={12} className={classes.filtersRow}>
              <Visible when={filters && filters.length > 0}>
                <Tooltip title="Clear all filters">
                  <IconButton
                    onClick={() => handleChange([])}
                    data-testid="clear-filters"
                  >
                    <ClearAllIcon />
                  </IconButton>
                </Tooltip>
              </Visible>
              {filters
                .filter((f) => f.category !== 'target_systems')
                .map((f) => {
                  return (
                    <Chip
                      disabled={isLoading}
                      icon={f.icon}
                      key={`${f.category}-${f.value}`}
                      label={f.description ? f.description : f.value}
                      className={classes.filterChip}
                      onDelete={() => {
                        handleChange(deleteFilter(f, filters));
                      }}
                    />
                  );
                })}
            </Grid>
          </Grid>
        </Toolbar>
      </>
    );
  }
);

export const lastStatusCodeFilterWrapper = (
  filters: Filter[],
  targetSystems: MedisanteTargetSystem[]
) => {
  const output: Filter[] = [];

  filters.forEach((f) => {
    if (f.category === 'last_status_code') {
      targetSystems
        .filter(
          (ts) => ts.lastStatusCode && ts.lastStatusCode.toString() === f.value
        )
        .forEach((ts) => {
          output.push({
            category: 'target_systems',
            value: ts.id,
          });
        });
    } else {
      output.push(f);
    }
  });
  return output;
};

const getCount = (filters: Filter[], categories: string[]): string => {
  const count = filters.filter(
    (f) => categories.indexOf(f.category) > -1
  ).length;

  return count > 0 ? `(${count})` : '';
};

export default Filters;
